// Shared UI primitives for Stewardship OS.
//
// All components here are BORING by design — per CLAUDE.md "Clarity
// over cleverness." Every future page composes these; don't grow this
// file into a "design system framework." When a component's API needs
// variants, add an explicit prop, not an abstraction.
//
// April 2026 design refresh added: Eyebrow, Swoosh, PageHeader,
// KpiTile, Chip, Segmented, Avatar (with per-staff gradients),
// SwBanner, TaskRow, TaskGroup, Kanban, KColumn, RCard. The file is
// now at ~17 primitives, just past the "split into folder" threshold
// that comment-history mentions. Splitting is a follow-up; for now
// the single-file idiom keeps imports trivial.

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, Copy } from "lucide-react";

import { MODE_LABEL, modeLabel, type Mode } from "./design/modes";
import { resolveMediaUrl } from "./lib/media-url";
import { DateField } from "./components/DateField";
import type { ChangeEvent } from "react";

export { modeLabel, type Mode };

// ─── Layout ─────────────────────────────────────────────────────────────

export function Stack({
  children,
  gap = 12,
}: PropsWithChildren<{ gap?: number }>) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap }}>
      {children}
    </div>
  );
}

export function Row({
  children,
  gap = 12,
  align = "center",
  wrap = true,
  justify,
}: PropsWithChildren<{
  gap?: number;
  align?: "start" | "center" | "end" | "baseline";
  wrap?: boolean;
  justify?: "start" | "center" | "end" | "space-between";
}>) {
  return (
    <div
      style={{
        display: "flex",
        gap,
        alignItems: align,
        flexWrap: wrap ? "wrap" : "nowrap",
        ...(justify ? { justifyContent: justify } : {}),
      }}
    >
      {children}
    </div>
  );
}

// ─── Card ───────────────────────────────────────────────────────────────
//
// `interactive` enables the brand hover treatment (blue border + blue
// glow + lift) — opt in only when the card is clickable so we don't
// spam hover effects across read-only sections. `as` lets the caller
// render a different tag (e.g., `as="article"` or `as="div"`) without
// breaking semantics.

export function Card({
  children,
  className = "",
  interactive = false,
  id,
}: PropsWithChildren<{
  className?: string;
  interactive?: boolean;
  /** Optional DOM id — lets callers scroll a specific card into view
   *  (e.g. an NBA "go here" action). Purely additive; omit for the default. */
  id?: string;
}>) {
  const cls = `card ${interactive ? "is-interactive" : ""} ${className}`.trim();
  return (
    <section className={cls} {...(id ? { id } : {})}>
      {children}
    </section>
  );
}

// ─── Pill ───────────────────────────────────────────────────────────────
//
// Used for relationship state, fit assessment, household role, etc.
// `variant` maps to semantic CSS classes defined in styles.css — new
// variants land when a new concept needs one, not via theme tokens.

export type PillVariant =
  | "neutral"
  // attention — a stated contradiction the reader needs to notice and
  // resolve (e.g. a member carrying the primary role while the household
  // designates someone else). Warning-toned, not danger: nothing is
  // broken, something disagrees.
  | "attention"
  | "state-active_client"
  | "state-prospect"
  | "state-past_client"
  | "state-engaged_lost"
  | "state-not_engaged"
  | "fit-ideal_fit"
  | "fit-typical"
  | "fit-poor_fit"
  | "fit-unassessed";

export function Pill({
  children,
  variant = "neutral",
  title,
}: PropsWithChildren<{ variant?: PillVariant; title?: string | undefined }>) {
  const className = variant === "neutral" ? "pill" : `pill ${variant}`;
  return (
    <span className={className} {...(title ? { title } : {})}>
      {children}
    </span>
  );
}

// ─── Button ─────────────────────────────────────────────────────────────
//
// Variants:
//   * primary  — solid blue, white text (legacy default; April 2026
//                refresh moved its styling out of the global `button`
//                rule into a `.btn-primary` class — applied here)
//   * ghost    — transparent / outlined (legacy "btn-ghost")
//   * danger / danger-ghost — error-state actions (legacy)
//   * sw-primary — gradient blue, uppercase, 11px, navy-tinted shadow
//   * sw-navy    — solid navy, white text, 700
//   * sw-default — white card with mid-gray border (use for low-emphasis
//                  internal-tool actions like filter toggles)
//   * sw-ghost   — fully transparent, no border (icon buttons)
//
// `size="sm"` and `iconOnly` modify the dimensions per the handoff
// (28px sm, 32x32 square icon). Both work with all sw-* variants.

type ButtonVariant =
  | "primary"
  | "ghost"
  | "danger"
  | "danger-ghost"
  | "sw-primary"
  | "sw-navy"
  | "sw-default"
  | "sw-ghost"
  // sw-danger — the modern (.btn family) destructive button: solid brand
  // red, white label, uppercase. Token-driven (var(--danger)); used by
  // ConfirmDialog. The legacy `danger` variant stays for existing call
  // sites.
  | "sw-danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "default" | "sm";
  iconOnly?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

export function Button({
  variant = "primary",
  size = "default",
  iconOnly = false,
  iconLeft,
  iconRight,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  const classes: string[] = [];
  // Legacy variants (preserve behavior for existing call sites).
  // April 2026: variant="primary" now adds an explicit .btn-primary
  // class. The bare `button` element used to ship this styling
  // globally; that pattern leaked into every clickable card and
  // chip in the app (Pulse-tile bug PR #140). The class-based
  // approach makes the styling opt-in and keeps the global selector
  // a no-op reset.
  if (variant === "primary") classes.push("btn-primary");
  else if (variant === "ghost") classes.push("btn-ghost");
  else if (variant === "danger") classes.push("btn-danger");
  else if (variant === "danger-ghost") classes.push("btn-danger-ghost");
  // Handoff variants — `.btn` base + modifier.
  else if (variant === "sw-primary") classes.push("btn", "btn--primary");
  else if (variant === "sw-navy") classes.push("btn", "btn--navy");
  else if (variant === "sw-default") classes.push("btn");
  else if (variant === "sw-ghost") classes.push("btn", "btn--ghost");
  else if (variant === "sw-danger") classes.push("btn", "btn--danger");
  if (size === "sm") classes.push("btn--sm");
  if (iconOnly) classes.push("btn--icon");
  if (className) classes.push(className);
  // A TEXT label rides in its own span so a squeezed button trims its text
  // instead of painting out through the side of its container (2026-09-09
  // — Cambria's household screen, where "+ New processing record" rendered
  // across the gap into the neighbouring card). `.btn__label` carries the
  // overflow/ellipsis; `.btn` carries min-width: 0 + max-width: 100%.
  //
  // ONLY a string/number child is wrapped, and that restriction is load-
  // bearing rather than caution. ~16 call sites pass an icon as a CHILD
  // (`<Button><Plus size={14} /> Add</Button>`) instead of via iconLeft.
  // Wrapping those puts icon and text inside ONE flex item, so the button's
  // own `gap: 6px` no longer sits between them — measured at exactly 6px
  // narrower, i.e. the icon visibly closes up against the label. Leaving
  // element children untouched keeps their DOM byte-identical.
  //
  // Containment does not depend on this: min-width/max-width on `.btn`
  // itself stop ANY button escaping its container. The span only upgrades
  // the failure from a hard clip to an ellipsis, and a text label is the
  // only case where that reads as intended rather than as damage.
  const label =
    !iconOnly && (typeof children === "string" || typeof children === "number") ? (
      <span className="btn__label">{children}</span>
    ) : (
      children
    );
  return (
    <button {...rest} type={rest.type ?? "button"} className={classes.join(" ").trim()}>
      {iconLeft}
      {label}
      {iconRight}
    </button>
  );
}

// One clipboard-copy button, shared by every screen that hands someone a
// URL (advisor personal booking links, admin booking-routing topic
// links, …) — a single place for the copy/"Copied" feedback so it
// behaves identically everywhere instead of being re-implemented per
// page. `compact` renders icon-only (for a tight row like a collapsed
// list item); the default renders the icon plus a text label.
export function CopyLinkButton({
  url,
  label = "Copy link",
  compact = false,
}: {
  url: string;
  label?: string;
  compact?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const icon = copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />;
  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly={compact}
      iconLeft={compact ? undefined : icon}
      aria-label={compact ? (copied ? "Copied" : label) : undefined}
      onClick={() => {
        void navigator.clipboard?.writeText(url).then(
          () => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          },
          () => {
            // Clipboard denied — the link is on screen to copy by hand.
          },
        );
      }}
    >
      {compact ? icon : copied ? "Copied" : label}
    </Button>
  );
}

// ─── Form field ─────────────────────────────────────────────────────────

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
}

export function FormField({ label, hint, id, ...inputProps }: FormFieldProps) {
  const inputId =
    id ?? `field-${label.replace(/\s+/g, "-").toLowerCase()}-${inputProps.name ?? ""}`;
  // DECISION (Grant, 2026-09-30): every date box types AND picks. Callers keep
  // their existing `onChange={(e) => ... e.target.value}`; DateField reports
  // the ISO string, so it is handed back wrapped in an event-shaped object.
  const { type, onChange, value, ...rest } = inputProps;
  const control =
    type === "date" ? (
      <DateField
        id={inputId}
        name={rest.name}
        required={rest.required}
        disabled={rest.disabled}
        value={typeof value === "string" ? value : ""}
        onChange={(iso) =>
          onChange?.({ target: { value: iso }, currentTarget: { value: iso } } as unknown as ChangeEvent<HTMLInputElement>)
        }
      />
    ) : (
      <input id={inputId} type={type} value={value} onChange={onChange} {...rest} />
    );
  return (
    <div className="form-row">
      <label htmlFor={inputId}>{label}</label>
      {control}
      {hint && <span className="muted">{hint}</span>}
    </div>
  );
}

// ─── Error banner ───────────────────────────────────────────────────────

interface ErrorBannerProps {
  children?: ReactNode;
  message?: string;
  action?: ReactNode;
}

export function ErrorBanner({ children, message, action }: ErrorBannerProps) {
  const body = children ?? message;
  if (!body) return null;
  return (
    <div className="error">
      <span>{body}</span>
      {action && <span className="error-action">{action}</span>}
    </div>
  );
}

// ─── Page shell ─────────────────────────────────────────────────────────
//
// Topbar + main container for every authenticated page. Sign-in / other
// unauth pages render without the shell.

interface PageShellProps {
  userLabel: string;
  onSignOut: () => void;
  children: ReactNode;
  headerExtras?: ReactNode;
}

export function PageShell({
  userLabel,
  onSignOut,
  children,
  headerExtras,
}: PageShellProps) {
  return (
    <>
      <header className="topbar">
        <h1>Stewardship OS</h1>
        <Row gap={12}>
          {headerExtras}
          <span className="user">{userLabel}</span>
          <Button variant="ghost" onClick={onSignOut}>
            Sign out
          </Button>
        </Row>
      </header>
      <main className="shell">{children}</main>
    </>
  );
}

// ─── Loading / empty states ─────────────────────────────────────────────

export function LoadingBlock({ children = "Loading…" }: PropsWithChildren) {
  return <div className="loading">{children}</div>;
}

// ─── EmptyState ─────────────────────────────────────────────────────────
//
// Shared "nothing here yet" primitive (audit item 8). Standardizes the
// ~20 bespoke empty states that each hand-rolled a centered glyph +
// headline + sub. Mirrors the ListView empty-state visual language
// (.empty-state-* reuse the same tokens as .list-view-empty-*). Pass an
// `icon` (usually a Lucide glyph), a `headline`, an optional `sub`
// (string or JSX), and an optional `action` node (a button / link).
interface EmptyStateProps {
  icon?: ReactNode;
  headline: ReactNode;
  sub?: ReactNode;
  /** Optional call-to-action rendered under the sub (e.g. a button). */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon,
  headline,
  sub,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div className={`empty-state${className ? ` ${className}` : ""}`}>
      {icon ? (
        <div className="empty-state-glyph" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      <div className="empty-state-headline">{headline}</div>
      {sub ? <div className="empty-state-sub">{sub}</div> : null}
      {action ? <div className="empty-state-action">{action}</div> : null}
    </div>
  );
}

// ─── Skeleton loaders (nav-redesign PR 10) ──────────────────────────────
// A shimmer block. Compose these for page/content skeletons so a loading
// screen reads as "the app is loading", not "stuck".
export function Skeleton({
  width = "100%",
  height = 16,
  radius = 6,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
}) {
  return (
    <div
      className="skeleton"
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

// Full-page app-shell silhouette shown during the auth/session boot,
// replacing the bare "Loading…" text. Mimics the top bar + sidebar +
// content so the first paint looks like the app arriving.
export function AppBootSkeleton() {
  return (
    <div
      className="boot-skeleton"
      role="status"
      aria-busy="true"
      aria-label="Loading Stewardship OS"
    >
      <div className="boot-skeleton__topbar" />
      <div className="boot-skeleton__body">
        <div className="boot-skeleton__sidebar">
          <Skeleton height={13} width="55%" />
          <Skeleton height={13} width="80%" />
          <Skeleton height={13} width="70%" />
          <Skeleton height={13} width="85%" />
          <Skeleton height={13} width="60%" />
          <Skeleton height={13} width="75%" />
        </div>
        <div className="boot-skeleton__main">
          <Skeleton height={26} width="38%" />
          <Skeleton height={110} />
          <Skeleton height={110} />
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Design system v2 primitives (April 2026 handoff).
// Each component below is a thin shell over the matching CSS class
// in styles.css. Avoid adding business logic here.
// ════════════════════════════════════════════════════════════════════════

// ─── Eyebrow ────────────────────────────────────────────────────────────
// Uppercase 10.5px blue label. Sits above an H1.
export function Eyebrow({ children }: PropsWithChildren) {
  return <div className="sw-eyebrow">{children}</div>;
}

// ─── Swoosh ─────────────────────────────────────────────────────────────
// Wraps the key word in a heading with a yellow skewed underline.
// Use sparingly — one swoosh per page, on the most important word.
export function Swoosh({ children }: PropsWithChildren) {
  return <span className="sw-swoosh">{children}</span>;
}

// ─── PageHeader ─────────────────────────────────────────────────────────
// Eyebrow + title + optional date row + actions. Replaces ad-hoc
// `<header className="page-header">` markup across pages.
export function PageHeader({
  eyebrow,
  title,
  date,
  actions,
  level = 1,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  date?: ReactNode;
  actions?: ReactNode;
  /** Use 2 for a second header on a page that already has its one H1. */
  level?: 1 | 2;
}) {
  const Heading = level === 2 ? "h2" : "h1";
  return (
    <header className="page-header">
      <div className="page-header__lead">
        {eyebrow && <div className="page-header__eyebrow">{eyebrow}</div>}
        <Heading>{title}</Heading>
        {date && <div className="page-header__date">{date}</div>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

// ─── Service Records: ModeShell + RecordHeader ─────────────────────────
//
// The Service Records design system runs on three workflow modes:
//
//   * "opp"  — Opportunity (blue)
//   * "proc" — Processing  (amber)
//   * "care" — Ongoing Care (green)
//
// `Mode` + `modeLabel()` live in a sibling `.ts` file (web/src/design/
// modes.ts) so node-only test files in tests/web/ can import them
// without pulling in JSX. We re-export them here so the rest of the
// codebase still imports from "ui" ergonomically.
//
// CSS-side mirrors live in styles.css (`--mode-opp`, `--mode-opp-soft`,
// `--mode-opp-deep` + matching `proc` and `care`) and color-value TS
// mirrors live in web/src/design/tokens.ts `modes`.

// ─── RecordHeader ──────────────────────────────────────────────────────
//
// The fixed-chrome hero block on every Service Record detail page.
// A 4px top bar carries the mode color; everything below sits on white
// card chrome. The breadcrumb + title + mode pill + chip row together
// answer four of Perplexity's six "above the fold" employee questions:
// what is this · which household is it for · what stage/status is it
// in · who owns it (chip row is where owner/avatar lands).
//
// The remaining two (what is missing/blocked · what happens next) are
// the seven employee modules that mount below this header in
// ModeShell.Modules — they land in PR 3.

export interface RecordHeaderProps {
  /** Mode drives the top bar color + mode pill chrome. */
  mode: Mode;
  /** Breadcrumb content (links + separators). Lives above the title. */
  breadcrumb?: ReactNode;
  /** The record title — usually the product noun (Home Policy, Refinance Mortgage, etc.). */
  title: ReactNode;
  /** Optional secondary chips (advisor, status, dates, fit assessment, etc.). */
  chips?: ReactNode;
  /** Optional right-side actions slot (e.g., CleanupActionsButton). */
  actions?: ReactNode;
  /** The people this record is for — the PersonStrip tile (X0: contact
   *  info is the top tile of every record, same as the household). */
  people?: ReactNode;
  /** "Also working with" — what else this household has in flight, as
   *  one chip row (Nate's spec §3). Sits between the chip row and the
   *  people tile, which is where his mockup puts it. */
  alsoWorkingWith?: ReactNode;
  /** A strip along the bottom edge of the header (the service record's
   *  Ongoing Care "Plans" line, v2 step 4). Part of the fixed chrome, so
   *  no view preference can hide it. */
  footer?: ReactNode;
}

export function RecordHeader({
  mode,
  breadcrumb,
  title,
  chips,
  actions,
  people,
  alsoWorkingWith,
  footer,
}: RecordHeaderProps) {
  return (
    <header className="record-header" data-mode={mode}>
      <div className="record-header__top-bar" />
      <div className="record-header__body">
        {breadcrumb && (
          <nav
            aria-label="Breadcrumb"
            className="record-header__breadcrumb"
          >
            {breadcrumb}
          </nav>
        )}
        <div className="record-header__title-row">
          {/* Title + mode pill share their own wrapping group so the
              actions column never fights the title text for a line
              break (2026-09-06 fix — see .record-header__actions below
              for why this existed unstyled). */}
          <div className="record-header__title-group">
            <h1 className="record-header__title">{title}</h1>
            <span className="record-header__mode-pill" data-mode={mode}>
              {MODE_LABEL[mode]}
            </span>
          </div>
          {actions && <div className="record-header__actions">{actions}</div>}
        </div>
        {chips && <div className="record-header__chip-row">{chips}</div>}
        {alsoWorkingWith}
        {people && <div className="record-header__people">{people}</div>}
        {footer}
      </div>
    </header>
  );
}

// ─── ModeShell ─────────────────────────────────────────────────────────
//
// The one-page-shell-rule wrapper for every Service Record detail page.
// Same structural skeleton across Opportunity / Processing / Ongoing
// Care; the `mode` prop drives:
//
//   1. Top-bar color (via the `.mode-shell--{mode}` class which sets
//      `--mode` / `--mode-soft` / `--mode-deep` CSS variables that
//      cascade into RecordHeader and any mode-aware child).
//   2. Which heading component the `<ModeShell.Heading>` slot renders
//      (StageBoard for opp, MilestoneTracker for proc, AnnualTimeline
//      for care — those components land in PR 2; PR 1 ships the slots).
//
// Composition:
//
//   <ModeShell mode="opp">
//     <ModeShell.Header>
//       <RecordHeader ... />
//     </ModeShell.Header>
//     <ModeShell.Heading>
//       {/* StageBoard / MilestoneTracker / AnnualTimeline */}
//     </ModeShell.Heading>
//     <ModeShell.Modules>
//       {/* Seven employee modules — main column + right rail */}
//     </ModeShell.Modules>
//     <ModeShell.Tabs>
//       {/* Documents / Notes / Audit / History */}
//     </ModeShell.Tabs>
//   </ModeShell>
//
// The slot pattern is a child-as-property idiom. Order is enforced by
// CSS gap-stacking, not by the runtime — any slot can be omitted.

export interface ModeShellProps {
  mode: Mode;
  children: ReactNode;
  /** Optional className override; merges with `.mode-shell .mode-shell--{mode}`. */
  className?: string;
}

interface ModeShellSlotProps {
  children: ReactNode;
}

function ModeShellHeader({ children }: ModeShellSlotProps) {
  return <div className="mode-shell__header">{children}</div>;
}
function ModeShellHeading({ children }: ModeShellSlotProps) {
  return <div className="mode-shell__heading">{children}</div>;
}
function ModeShellModules({ children }: ModeShellSlotProps) {
  return <div className="mode-shell__modules">{children}</div>;
}
function ModeShellTabs({ children }: ModeShellSlotProps) {
  return <div className="mode-shell__tabs">{children}</div>;
}

export function ModeShell({ mode, children, className }: ModeShellProps) {
  const cls = [
    "mode-shell",
    `mode-shell--${mode}`,
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls} data-mode={mode}>
      {children}
    </div>
  );
}

ModeShell.Header = ModeShellHeader;
ModeShell.Heading = ModeShellHeading;
ModeShell.Modules = ModeShellModules;
ModeShell.Tabs = ModeShellTabs;

// ─── Avatar ─────────────────────────────────────────────────────────────
// Circular initials with optional per-staff gradient. The `person`
// enum maps to gradient classes in styles.css. New staff: register a
// gradient AND extend the enum here.

export type AvatarPerson =
  | "default"
  | "grant"
  | "joy"
  | "brian"
  | "donna"
  | "mike"
  | "nate";

export type AvatarSize = "xs" | "sm" | "md" | "lg";

export function Avatar({
  initials,
  person = "default",
  size = "md",
  title,
  image = null,
}: {
  initials: string;
  person?: AvatarPerson;
  size?: AvatarSize;
  title?: string;
  // When set, the user's uploaded profile photo fills the circle and the
  // initials/gradient become the fallback (shown until/unless an image
  // exists). Pass the raw users.image value — resolveMediaUrl handles the
  // cross-origin-dev prefix.
  image?: string | null;
}) {
  const src = resolveMediaUrl(image);
  return (
    <span
      className={`avatar avatar--${size} avatar--${person}`}
      title={title}
      aria-label={title ?? initials}
    >
      {src ? (
        <img className="avatar-img" src={src} alt="" />
      ) : (
        initials.toUpperCase()
      )}
    </span>
  );
}

// ─── Chip ───────────────────────────────────────────────────────────────
// Filter pill — 28px tall, mid-gray border at rest, navy fill when
// active. `flagged` adds the brand "yellow active" treatment for
// flagged-only filters.

export function Chip({
  children,
  active = false,
  flagged = false,
  onClick,
  type = "button",
  ...rest
}: PropsWithChildren<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    active?: boolean;
    flagged?: boolean;
  }
>) {
  const cls = [
    "chip",
    flagged ? "chip--flagged" : "",
    active ? "is-active" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button {...rest} type={type} className={cls} onClick={onClick}>
      {children}
    </button>
  );
}

// ─── Segmented ──────────────────────────────────────────────────────────
// Light-gray container with white "pill" on active button. The
// generic `T` lets callers pick any value type.

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  /** `tone` colors the SELECTED state — "on" fills the same green the
   *  automation list's toggle uses, "off" fills red. Reserve it for a
   *  choice that IS an on/off decision (an automation's turn-on-after-
   *  save control): elsewhere the neutral navy/white active state is
   *  correct, and coloring options that aren't literally on/off (a
   *  scope picker, a date range) would just be noise. Omit for the
   *  default neutral look. */
  options: {
    value: T;
    label: ReactNode;
    icon?: ReactNode;
    tone?: "on" | "off";
  }[];
  value: T;
  onChange: (next: T) => void;
  ariaLabel?: string;
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className="seg">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="tab"
            aria-selected={active}
            className={
              active
                ? `is-active${opt.tone ? ` is-active--${opt.tone}` : ""}`
                : ""
            }
            onClick={() => onChange(opt.value)}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── KpiTile ────────────────────────────────────────────────────────────
// White card with 32px icon top-left, optional trend pill top-right,
// 44px navy big number, label, optional breakdown row. Composable —
// pass any ReactNode for icon/trend/breakdown.

export function KpiTile({
  icon,
  trend,
  value,
  unit,
  label,
  breakdown,
  onClick,
  className = "",
}: {
  icon?: ReactNode;
  trend?: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  label: ReactNode;
  breakdown?: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const interactive = !!onClick;
  return (
    <div
      className={`tile ${interactive ? "is-interactive" : ""} ${className}`.trim()}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      {(icon || trend) && (
        <div className="tile__top">
          {icon && <div className="tile__icon">{icon}</div>}
          {trend && <div className="tile__trend">{trend}</div>}
        </div>
      )}
      <div className="tile__number">
        {value}
        {unit && <small>{unit}</small>}
      </div>
      <div className="tile__label">{label}</div>
      {breakdown && <div className="tile__breakdown">{breakdown}</div>}
    </div>
  );
}

// ─── Banner ─────────────────────────────────────────────────────────────
// Navy-bg system callout. Yellow icon left, yellow CTA right. Use for
// app-level notices that aren't dismissable inline.

export function SwBanner({
  icon,
  children,
  action,
}: PropsWithChildren<{ icon?: ReactNode; action?: ReactNode }>) {
  return (
    <div className="sw-banner" role="status">
      {icon}
      <span>{children}</span>
      {action}
    </div>
  );
}

// ─── Zone ───────────────────────────────────────────────────────────────
// Dashboard zone = section wrapper with optional title row + actions.

export function Zone({
  title,
  sub,
  actions,
  children,
  className = "",
}: PropsWithChildren<{
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  className?: string;
}>) {
  return (
    <section className={`zone ${className}`.trim()}>
      {(title || sub || actions) && (
        <div className="zone__head">
          <div className="zone__titlewrap">
            {title && <h2 className="zone__title">{title}</h2>}
            {sub && <span className="zone__sub">{sub}</span>}
          </div>
          {actions && <div className="zone__actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// ─── Tiles grid ─────────────────────────────────────────────────────────

export function Tiles({
  children,
  columns = 4,
}: PropsWithChildren<{ columns?: 2 | 3 | 4 }>) {
  const cls = columns === 4 ? "tiles" : `tiles tiles--${columns}`;
  return <div className={cls}>{children}</div>;
}

// ─── Tabs (with swoosh underline) ──────────────────────────────────────

export function Tabs({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: { value: string; label: ReactNode }[];
  value: string;
  onChange: (next: string) => void;
  ariaLabel?: string;
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className="sw-tabs">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={value === opt.value}
          className={`sw-tab${value === opt.value ? " is-active" : ""}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Overlays — Modal, Drawer, Tooltip
// Spec: OVERLAYS.md (April 2026 design refresh).
// All three render via React.createPortal to document.body so they
// escape ancestor `overflow: hidden` / transform stacking contexts and
// stack against the page-level z-index ladder predictably.
// ════════════════════════════════════════════════════════════════════════

// ─── Shared overlay helpers ────────────────────────────────────────────

/**
 * Returns true on the server / before hydration. We call this in the
 * portal-using components so SSR doesn't crash on `document` access.
 * The current app is fully client-rendered, but defending against SSR
 * costs nothing and unblocks future Pages-Functions SSR if it lands.
 */
function useIsClient(): boolean {
  const [client, setClient] = useState(false);
  useEffect(() => setClient(true), []);
  return client;
}

/**
 * Lock document scroll while at least one modal/drawer is open. Uses
 * a ref-counted approach so two stacked overlays don't fight each
 * other. Restores the prior overflow value on the LAST close.
 *
 * Locks <html> ONLY (2026-10-27 fix of the 2026-09-06 fix below, which
 * locked BOTH). With both locked, <body> became its own scroll container
 * and the sticky top navigation stopped sticking: opening a dialog on a
 * scrolled Marketing Hub page slid the top bar 500px off screen (audit
 * S1-M3, reproduced on staging). <html> alone does the locking.
 *
 * The 2026-09-06 note, kept because it explains why <html> is the one:
 * (originally "Locks BOTH <body> and <html>"). Locking only <body>
 * looks right and IS the standard technique — except this app's global
 * mobile-safety rule sets `overflow-x: clip` on `html` (the "no
 * horizontal page panning" guard, styles.css ~line 468) with `overflow-y`
 * left unset. Per the CSS overflow spec, when one axis is non-visible and
 * the other isn't set, the unset axis computes to `auto`, not `visible` —
 * so <html> silently gains its OWN independent vertical scrollbox the
 * moment that rule is present, and browsers stop propagating <body>'s
 * overflow to the viewport once <html> has any non-visible overflow of
 * its own. The practical effect: setting body.style.overflow = "hidden"
 * did nothing, and scrolling inside an open modal scrolled the page
 * behind it instead — reported live by Grant on the automations email
 * editor. Locking documentElement too closes that gap regardless of
 * which element the browser treats as the real scroller.
 */
let scrollLockCount = 0;
let priorHtmlOverflow: string | null = null;
let priorHtmlPaddingRight: string | null = null;
function lockBodyScroll(): void {
  if (typeof document === "undefined") return;
  if (scrollLockCount === 0) {
    const root = document.documentElement;
    priorHtmlOverflow = root.style.overflow;
    priorHtmlPaddingRight = root.style.paddingRight;
    // Hiding the scrollbar widens the page by its width, and everything
    // shifts sideways as the dialog opens. Give that width back as padding.
    const scrollbar = window.innerWidth - root.clientWidth;
    root.style.overflow = "hidden";
    if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
  }
  scrollLockCount += 1;
}
function unlockBodyScroll(): void {
  if (typeof document === "undefined") return;
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    const root = document.documentElement;
    root.style.overflow = priorHtmlOverflow ?? "";
    root.style.paddingRight = priorHtmlPaddingRight ?? "";
    priorHtmlOverflow = null;
    priorHtmlPaddingRight = null;
  }
}

/**
 * Focus trap utility — keeps Tab navigation inside the supplied
 * container while the overlay is open. Returns the element that had
 * focus when the trap was installed, so the caller can restore focus
 * on close. Light-weight (no third-party focus-trap dep).
 */
function focusableSelectors(): string {
  return [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(focusableSelectors()),
  );
}

// Exported so the second Modal primitive (components/Modal.tsx) can share
// the same focus-trap behavior without a full DOM/CSS reroute. Audit item 3.
export function useFocusTrap(
  active: boolean,
  containerRef: React.RefObject<HTMLElement | null>,
): void {
  useLayoutEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Move focus into the overlay on open. First focusable wins; if
    // none exist, focus the container itself so Esc still works.
    const targets = getFocusable(container);
    if (targets.length > 0) {
      // preventScroll: moving focus into the dialog must not scroll the page.
      targets[0]!.focus({ preventScroll: true });
    } else {
      container.tabIndex = -1;
      container.focus({ preventScroll: true });
    }
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key !== "Tab") return;
      const items = getFocusable(container!);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !container!.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [active, containerRef]);
}

function useEscToClose(active: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!active) return;
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active, onClose]);
}

// ─── Backdrop dismissal (shared by Modal + Drawer) ─────────────────────
//
// "Close when the backdrop is clicked" sounds like one check, but a
// browser fires `click` on the nearest common ancestor of where the
// mouse went DOWN and where it came UP. So a drag that starts inside the
// dialog (the resize corner of a textarea, a text selection that runs
// past the edge) and ends on the backdrop is delivered as a click ON the
// backdrop — and the dialog closed, taking the draft with it. Brian Ream,
// 2026-09-10: "when I go to drag the box to make it bigger, I'll release
// and then it will cause the pop up to close and I'll lose the notes."
//
// DECISION: require the press AND the release to land on the backdrop.
// The alternative — a global "dirty" flag that disables dismissal while
// a form has content — belongs to each dialog (it knows what a draft
// is); this fix is the one that's true for every overlay regardless.
function useBackdropDismiss(enabled: boolean, onClose: () => void) {
  const pressedOnBackdrop = useRef(false);
  return {
    onMouseDown: (e: ReactMouseEvent<HTMLDivElement>) => {
      pressedOnBackdrop.current = e.target === e.currentTarget;
    },
    onClick: (e: ReactMouseEvent<HTMLDivElement>) => {
      const pressed = pressedOnBackdrop.current;
      pressedOnBackdrop.current = false;
      if (!enabled) return;
      if (e.target === e.currentTarget && pressed) onClose();
    },
  };
}

// ─── Modal ─────────────────────────────────────────────────────────────
//
// Centered overlay with fade-in backdrop + rise-in card.
//
// Sizes (per OVERLAYS.md §5/§6):
//   * sm — 460px wide, for short confirmations / capture moments
//   * md — 640px wide, default
//   * lg — 960px wide, for triage lists like Attention Needed
//
// Behavior:
//   * Backdrop click closes (override with `dismissOnBackdrop={false}`)
//   * Esc closes (override with `dismissOnEsc={false}`)
//   * Focus is trapped inside while open
//   * Body scroll is locked while open
//   * `prefers-reduced-motion` is respected via the CSS @media block
//
// `header` / `footer` props are optional — pass JSX for richer headers
// (icon tile + title + sub) or footers (action button rows). When
// `title` is set without a `header`, we render the default header
// shape (icon optional, title required, close-X right-aligned).

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Sub-headline rendered under the title in the default header. */
  sub?: ReactNode;
  /** Icon rendered in the 32x32 tinted square left of the title. */
  icon?: ReactNode;
  /** Override the default header completely. */
  header?: ReactNode;
  /** Sticky footer slot (e.g., action buttons + summary count). */
  footer?: ReactNode;
  /** "sm" | "md" | "lg" — see the doc above. */
  size?: "sm" | "md" | "lg";
  /** Default true. Set false to require an explicit close action. */
  dismissOnBackdrop?: boolean;
  dismissOnEsc?: boolean;
  /** Center the body content (used by save-for-later / voice-memo). */
  centered?: boolean;
  /** Additional className on the .modal-shell. */
  className?: string;
  /**
   * Called with the close-button ref when the modal mounts. Useful for
   * tests that want to assert focus moved to the close button on open.
   */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  children?: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  sub,
  icon,
  header,
  footer,
  size = "md",
  dismissOnBackdrop = true,
  dismissOnEsc = true,
  centered = false,
  className = "",
  children,
}: ModalProps) {
  const isClient = useIsClient();
  const shellRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  useFocusTrap(open, shellRef);
  useEscToClose(open && dismissOnEsc, onClose);
  const backdrop = useBackdropDismiss(dismissOnBackdrop, onClose);

  useEffect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [open]);

  if (!isClient || !open) return null;

  const node = (
    <div
      className="modal-overlay"
      onMouseDown={backdrop.onMouseDown}
      onClick={backdrop.onClick}
      data-testid="modal-overlay"
    >
      <div
        ref={shellRef}
        className={`modal-shell modal--${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {header ?? (
          (title || icon) && (
            <div className="modal-header">
              <div className="modal-header__lead">
                {icon && <div className="modal-header__icon">{icon}</div>}
                <div>
                  {title && (
                    <h2 className="modal-header__title" id={titleId}>
                      {title}
                    </h2>
                  )}
                  {sub && <div className="modal-header__sub">{sub}</div>}
                </div>
              </div>
              <button
                type="button"
                className="modal-header__close"
                onClick={onClose}
                aria-label="Close"
                data-testid="modal-close"
              >
                <CloseIcon />
              </button>
            </div>
          )
        )}
        <div
          className={`modal-body${centered ? " modal-body--centered" : ""}`}
        >
          {children}
        </div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

// ─── Drawer ────────────────────────────────────────────────────────────
//
// Right-side slide-in sheet. Same API + behavior as Modal, but slides
// in from the right (480px wide on desktop, 100% on mobile). Use this
// for "inspect/edit a record without leaving the underlying screen"
// flows — contact preview from a list, household snapshot from a row.

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Right-side header actions (typically a primary save/action button). */
  headerActions?: ReactNode;
  /** Sticky footer slot. */
  footer?: ReactNode;
  /** Default true. Click the backdrop to close. */
  dismissOnBackdrop?: boolean;
  dismissOnEsc?: boolean;
  className?: string;
  children?: ReactNode;
}

export function Drawer({
  open,
  onClose,
  title,
  headerActions,
  footer,
  dismissOnBackdrop = true,
  dismissOnEsc = true,
  className = "",
  children,
}: DrawerProps) {
  const isClient = useIsClient();
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const backdrop = useBackdropDismiss(dismissOnBackdrop, onClose);
  useFocusTrap(open, sheetRef);
  useEscToClose(open && dismissOnEsc, onClose);

  useEffect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [open]);

  if (!isClient || !open) return null;

  const node = (
    <div
      className="drawer-overlay"
      onMouseDown={backdrop.onMouseDown}
      onClick={backdrop.onClick}
      data-testid="drawer-overlay"
    >
      <aside
        ref={sheetRef}
        className={`drawer-sheet ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        <header className="drawer-header">
          <button
            type="button"
            className="drawer-header__close"
            onClick={onClose}
            aria-label="Close"
            data-testid="drawer-close"
          >
            <CloseIcon />
          </button>
          {title && (
            <h2 className="drawer-header__title" id={titleId}>
              {title}
            </h2>
          )}
          {headerActions && (
            <div className="drawer-header__actions">{headerActions}</div>
          )}
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-footer">{footer}</div>}
      </aside>
    </div>
  );

  return createPortal(node, document.body);
}

// ─── Tooltip ───────────────────────────────────────────────────────────
//
// Custom navy-on-white tooltip that replaces the browser's native
// `title=""` attribute. Pure-CSS positioning with `placement` prop
// (no @floating-ui dep yet — add later if collision detection is
// needed). Tooltip mounts INLINE next to the trigger (not portaled)
// so it inherits the trigger's stacking context.
//
// Behavior:
//   * Hover: 200ms delay, then fades in over 120ms
//   * Focus: 0ms delay (keyboard users get the affordance immediately)
//   * Mouse-leave or blur: hides immediately
//   * Never traps focus, never blocks click-through (pointer-events: none)
//   * Honors `prefers-reduced-motion` via the @media block in styles.css
//
// Usage:
//
//   <Tooltip label="Export to Excel · ⇧⌘E">
//     <button className="btn--icon">…</button>
//   </Tooltip>
//
// `label` can be a string OR ReactNode. Long strings wrap up to 240px
// per the spec.

const TOOLTIP_HOVER_DELAY_MS = 200;

interface TooltipProps {
  label: ReactNode;
  children: ReactNode;
  /** Default "top". Tooltip arrows match the placement. */
  placement?: "top" | "bottom" | "left" | "right";
  /** Disable the tooltip without unwrapping the trigger. */
  disabled?: boolean;
}

export function Tooltip({
  label,
  children,
  placement = "top",
  disabled = false,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  const hoverTimer = useRef<number | null>(null);

  const show = useCallback(
    (delay: number) => {
      if (disabled || !label) return;
      if (hoverTimer.current !== null) {
        window.clearTimeout(hoverTimer.current);
      }
      if (delay <= 0) {
        setOpen(true);
        return;
      }
      hoverTimer.current = window.setTimeout(() => setOpen(true), delay);
    },
    [disabled, label],
  );
  const hide = useCallback(() => {
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    setOpen(false);
  }, []);

  // Hide on Esc — gives keyboard users a way to dismiss without losing
  // focus. We don't trap focus (tooltips never should).
  const onKeyDown = (e: ReactKeyboardEvent<HTMLSpanElement>) => {
    if (e.key === "Escape" && open) hide();
  };

  if (disabled || !label) {
    return <>{children}</>;
  }

  return (
    <span
      className="sw-tooltip-wrap"
      onMouseEnter={() => show(TOOLTIP_HOVER_DELAY_MS)}
      onMouseLeave={hide}
      onFocus={() => show(0)}
      onBlur={hide}
      onKeyDown={onKeyDown}
      // aria-describedby is wired automatically — when the tooltip is
      // open we set it on the wrapper so the embedded trigger inherits
      // the description. (Native title="" tooltips offer this for free;
      // we restore it here.)
      aria-describedby={open ? tooltipId : undefined}
    >
      {children}
      {open && (
        <span
          className={`sw-tooltip sw-tooltip--${placement}`}
          role="tooltip"
          id={tooltipId}
        >
          {label}
        </span>
      )}
    </span>
  );
}

// ─── Inline icons used by Modal / Drawer ────────────────────────────────

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ─── ChartTooltip ──────────────────────────────────────────────────────
//
// Pinned/transient tooltip for chart segments per OVERLAYS.md §2.
// The CSS classes (.tchart__tip*) shipped earlier; this is the React
// API. Render position is `transform: translate(-50%, -100%)` above
// the cursor target — caller passes `x`/`y` (relative to the chart's
// containing element) and the tip auto-clamps inside the chart's
// bounds via the `clamp` prop (default 6%–94%). Pin behavior is
// controlled by the caller (state lives in the chart component).

export interface ChartTooltipProps {
  /** Pixel offset within the chart container (left edge = 0). */
  x: number;
  /** Pixel offset within the chart container (top edge = 0). */
  y: number;
  /** Color swatch for the head row (typically the bar/segment color). */
  swatchColor?: string;
  /** Top-row label (e.g., the company name or series name). */
  label: string;
  /** Right-of-head context (typically the month / data point label). */
  contextLabel?: ReactNode;
  /** Big-number value (e.g., "$1.24M"). */
  value: ReactNode;
  /** Optional context line below the value (e.g., "38% of Apr total"). */
  context?: ReactNode;
  /** When true, the tip stays open until `onClose` fires. Adds the
   *  close-X + CTA row that pinned tooltips show. */
  pinned?: boolean;
  /** Pinned-state CTA — typically a "View detail →" link. */
  cta?: ReactNode;
  /** Called when the close-X is clicked. Only relevant when pinned. */
  onClose?: () => void;
}

export function ChartTooltip({
  x,
  y,
  swatchColor,
  label,
  contextLabel,
  value,
  context,
  pinned = false,
  cta,
  onClose,
}: ChartTooltipProps) {
  return (
    <div
      role="dialog"
      aria-label="Segment details"
      className="tchart__tip"
      style={{ left: x, top: y }}
    >
      <div className="tchart__tip-head">
        {swatchColor && (
          <span
            className="tchart__tip-swatch"
            style={{ background: swatchColor }}
            aria-hidden="true"
          />
        )}
        <span>{label}</span>
        {contextLabel && (
          <span className="tchart__tip-mo">{contextLabel}</span>
        )}
        {pinned && onClose && (
          <button
            type="button"
            className="tchart__tip-close"
            onClick={onClose}
            aria-label="Close tooltip"
          >
            <CloseIcon />
          </button>
        )}
      </div>
      <div className="tchart__tip-value">{value}</div>
      {context && <div className="tchart__tip-pct">{context}</div>}
      {pinned && cta && <div className="tchart__tip-cta">{cta}</div>}
    </div>
  );
}

// ─── TweaksPanel + TweaksFab ──────────────────────────────────────────
//
// Per OVERLAYS.md §8: bottom-right floating helper panel + collapsed
// FAB. Persistent UX furniture (NOT a modal — does not trap focus,
// the user keeps interacting with the page). State lives in the
// caller; this is a thin presentational pair.

export interface TweaksPanelProps {
  /** When true, render the expanded panel; when false, render the FAB. */
  open: boolean;
  /** Title shown in the panel header (uppercase Gotham Bold 800 11px). */
  title: ReactNode;
  /** Toggle handler — fires on FAB click and on close-X. */
  onToggle: () => void;
  /** Optional drag-handle slot (a small "⠿" icon by default). */
  handleSlot?: ReactNode;
  /** Optional FAB icon (a small sparkle by default). */
  fabIcon?: ReactNode;
  /** Aria-label for the FAB when collapsed. */
  fabAriaLabel?: string;
  /** Body content — section headers + form rows + tips. */
  children?: ReactNode;
}

export function TweaksPanel({
  open,
  title,
  onToggle,
  handleSlot,
  fabIcon,
  fabAriaLabel = "Open helper panel",
  children,
}: TweaksPanelProps) {
  if (!open) {
    return (
      <button
        type="button"
        className="tweaks-fab"
        onClick={onToggle}
        aria-label={fabAriaLabel}
        data-testid="tweaks-fab"
      >
        {fabIcon ?? <SparkleIcon />}
      </button>
    );
  }
  return (
    <aside
      className="tweaks-panel"
      role="complementary"
      aria-label={typeof title === "string" ? title : undefined}
      data-testid="tweaks-panel"
    >
      <header className="tweaks-panel__header">
        {handleSlot ?? <span className="tweaks-panel__handle" aria-hidden="true">⠿</span>}
        <h3 className="tweaks-panel__title">{title}</h3>
        <button
          type="button"
          className="modal-header__close"
          onClick={onToggle}
          aria-label="Close helper panel"
        >
          <CloseIcon />
        </button>
      </header>
      <div className="tweaks-panel__body">{children}</div>
    </aside>
  );
}

/** Small section heading inside a TweaksPanel body. */
export function TweaksSectionTitle({ children }: PropsWithChildren) {
  return <div className="tweaks-panel__section-title">{children}</div>;
}

function SparkleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
      <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z" />
    </svg>
  );
}
